import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#scheduleDeactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#clearHoverTimers();
    this.#cleanupMutationObserver();
  }

  /**
   * Runoff: hover-intent timing.
   *
   * The mega-menu panel is full width, so the pointer often travels a long
   * diagonal from a top-level item (e.g. "For business", sitting mid-bar) down
   * to links pinned at the far-left edge of the panel. On the way it clips the
   * neighbouring items ("Gallery", "Artwork of your car"), and without a delay
   * each clip instantly swaps or closes the panel — so the links become hard
   * to reach.
   *
   * Fix (industry standard, see Baymard "Provide a Hover Delay of 300-500ms"
   * and NN/g "Timing Guidelines for Exposing Hidden Content"):
   *  - a short OPEN delay, so only a deliberate pause on another item switches
   *    the panel; a quick clip while sweeping toward the open panel does not;
   *  - a longer CLOSE delay, so a brief slip off the trigger or into the seam
   *    above the panel does not dismiss it.
   * Keyboard focus and the first open stay instant.
   */
  #openDelay = 140;
  #closeDelay = 260;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  #clearOpenTimer = () => {
    clearTimeout(this.#openTimer);
    this.#openTimer = undefined;
  };

  #clearCloseTimer = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  };

  #clearHoverTimers = () => {
    this.#clearOpenTimer();
    this.#clearCloseTimer();
  };

  /**
   * Deactivate the active item after the close delay, unless the pointer
   * returns to the menu or panel in the meantime.
   */
  #scheduleDeactivate = () => {
    this.#clearOpenTimer();
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => this.#deactivate(), this.#closeDelay);
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Handle a hover/focus on a menu item. Opens immediately on keyboard focus or
   * when no panel is open yet; otherwise waits out the open delay so a diagonal
   * sweep toward an already-open panel does not swap it. See #openDelay.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    // Pointer/focus is back inside the menu system - cancel a pending close.
    this.#clearCloseTimer();

    if (item == this.#state.activeItem) {
      this.#clearOpenTimer();
      return;
    }

    const openImmediately = event.type === 'focus' || this.#state.activeItem == null;

    this.#clearOpenTimer();

    if (openImmediately) {
      this.#activate(event, item);
    } else {
      this.#openTimer = setTimeout(() => this.#activate(event, item), this.#openDelay);
    }
  };

  /**
   * Activate the given menu item immediately.
   * @param {PointerEvent | FocusEvent} event
   * @param {HTMLElement} item
   */
  #activate = (event, item) => {
    if (!(event.target instanceof Element)) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after the close delay.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Keyboard: leaving via blur should still collapse without a lingering delay.
    if (event.type === 'blur') {
      const menu = findSubmenu(this.#state.activeItem);
      const stayingInMenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
      if (!stayingInMenu) this.#deactivate();
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu) return;

    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#clearHoverTimers();

    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
